import {put, call} from 'redux-saga/effects';
import Expense from "../../entities/expense";
import * as Actions from "../actions/expenseActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ExpenseAction = {
    expenseId: string;
    expense: Expense;
    expenses: Expense[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneExpense(action: ExpenseAction) {
	const session = tabSession.captureGeneration();
	try {
		const expense: Expense = yield call(api.findOne, 'expense', action.expenseId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.EXPENSE_FETCH_ONE_SUCCESS, expenseId: action.expenseId, expense});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.EXPENSE_FETCH_ONE_FAILURE, expenseId: action.expenseId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ExpenseAction} action
 */
export function* saveExpense(action: ExpenseAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ EXPENSE_SAVE saga', action.expense?.id);
    
	if (action.expense === undefined || action.expense === null) {
		console.error('saveExpense received a null');
		throw new Error('saveExpense received a null');
	}

	if (!(action.expense instanceof Expense)) {
		action.expense = new Expense(action.expense);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'expense', action.expense);
		if (addResult.isErr()) {
			console.error(`⮂ saveExpense: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.EXPENSE_SAVE_FAILURE, expense: action.expense, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.EXPENSE_SAVE, expense: action.expense});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.EXPENSE_SAVE_FAILURE, expense: action.expense, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {ExpenseAction} action
 * @param {Expense[]} action.expenses
 */
export function* saveManyExpenses(action: ExpenseAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ EXPENSE_SAVE_MANY saga', action.expenses.map(expense => expense.id).join(','));
    
    if (!Array.isArray(action.expenses)) {
        console.error('saveManyExpenses expected an array, received', action.expenses);
        throw new Error('saveManyExpenses expected an array');
    }

	try {
		const toSave: Expense[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.expenses.length; i++) {
			const expense = action.expenses[i];
			expense.persisted = false;
			toSave.push(expense);
		}

		const addResult = yield call(idb.add, 'expense', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyExpenses: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.EXPENSE_SAVE_MANY_FAILURE, expenses: action.expenses, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.EXPENSE_SAVE_MANY, expenses: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.EXPENSE_SAVE_MANY_FAILURE, expenses: action.expenses, status: 500, err});

		console.error(`Error saving ${action.expenses.length} expenses to database`, err);
	}
}

/**
 * @param {ExpenseAction} action
 */
export function* removeExpense(action: ExpenseAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.expense === undefined || action.expense === null || !(action.expense instanceof Expense)) {
			console.error('removeExpense expected type Expense, received', action.expense);
			throw new Error('removeExpense expected type Expense');
		}

	try {
		// Mark as deleted
		action.expense.status = 'D';
		action.expense.addedOn = newHRTimestamp();
		action.expense.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'expense', action.expense);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeExpense: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.EXPENSE_REMOVE_FAILURE, expense: action.expense, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.EXPENSE_REMOVE, expense: action.expense});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.EXPENSE_REMOVE_FAILURE, expense: action.expense, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Expense.
	*
	* @param {ExpenseAction} action
	*/
export function* restoreExpense(action: ExpenseAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreExpense expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.expense === undefined || action.expense === null) {
        console.error('restoreExpense expected a Expense, received null');
        throw new Error('restoreExpense expected a Expense, received null');
    }
    else if (!(action.expense instanceof Expense)) action.expense = new Expense(action.expense); 
    
	try {
		// Mark as restored
		action.expense.status = 'U';
		action.expense.addedOn = newHRTimestamp();
		action.expense.addedBy = userId;
		action.expense.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'expense', action.expense);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreExpense: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.EXPENSE_RESTORE_FAILURE, expense: action.expense, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.EXPENSE_RESTORE, expense: action.expense});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.EXPENSE_RESTORE_FAILURE, expense: action.expense, status: 500, err});

		console.error('Error restoring from database', err);
	}
}